<template>
  <article v-if="film" class="flex flex-col">
    <div class="mb-[120px] flex gap-[20px]">
      <div class="flex flex-col xl:w-3/4 2xl:w-4/5">
        <BaseTheContentBreadcrumbs :current-page-title="film.title" />
        <h1
          class="mb-[28px] text-[32px] font-medium leading-[38px]"
          v-text="film.title"
        />
        <CommonBlocksImageArticleBlock
          v-if="film.image"
          class="mb-[32px]"
          :published-at="film.published_at"
          :src="film.image.src"
        />
        <div v-if="correctSrc" class="mb-6 h-40 w-full md:h-136">
          <iframe
            v-lazy-load
            :src="correctSrc"
            width="100%"
            height="100%"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
        <div v-else class="text-[32px] font-medium leading-[38px]">
          Видео не найдено.
        </div>
        <div class="page-body mb-[65px]" v-html="film.body" />

        <ClientOnly>
          <div v-if="film.authors?.length" class="flex flex-col">
            <h3 class="mb-[36px] text-[24px] font-medium">Автор</h3>
            <div class="grid grid-flow-row gap-5">
              <CommonAuthorInfo
                v-for="author in film.authors"
                :key="author.id"
                :author="author"
              />
            </div>
          </div>
        </ClientOnly>
      </div>
      <CommonBlocksRightArticleBlock class="xl:w-1/4 2xl:w-1/5" />
    </div>

    <CommonBlocksPageFooterBlocks />
  </article>
</template>

<script setup>
definePageMeta({
  layout: 'marginless',
});

const route = useRoute();
const { secondsToISO } = helperSeo();
const slug = route?.params?.slug;
const { filmSlug } = useFilmBySlug(slug);
useErrorHandler(() => filmSlug.failureReason.value?.status);

const film = computed(() => filmSlug?.data?.value);

const correctSrc = computed(() => {
  if (film.value?.video_code?.includes('https')) {
    return film.value.video_code;
  }
  return false;
});
const title = computed(() => film.value?.title);
const seo = computed(() => film.value?.seo);
const robots = ref('all');

if (seo.value) {
  const arr = [];
  if (seo.value.index === false) arr.push('noindex');
  if (seo.value.follow === false) arr.push('nofollow');
  if (seo.value.archive === false) arr.push('noarchive');
  if (arr.length > 0) {
    robots.value = arr.reduce((previousValue, currentValue) => {
      return `${previousValue}, ${currentValue}`;
    });
  }
}

useHead(
  computed(() => ({
    title: `${title.value ? `${title.value} |` : ''} Смотреть ${
      route.path.split('/').includes('films') ? 'фильмы' : 'видеолекции'
    } по истории РФ для школьников и студентов`,
    meta: [
      {
        hid: 'og:type',
        property: 'og:type',
        content: 'video.other',
      },
      {
        name: 'ya:ovs:upload_date',
        content: film.value?.published_at,
      },
      { name: 'ya:ovs:adult', content: 'no' },
      {
        name: 'video:duration',
        content: film.value?.duration?.toString(),
      },
      { property: 'og:video:type', content: 'video/mp4' },
      { property: 'og:title', content: film.value?.title },
      {
        property: 'og:url',
        content: `${useHostApp()}/watch/films/${slug}`,
      },
      { property: 'og:video', content: correctSrc.value },
      {
        property: 'og:image',
        content: `${useHostApp()}${film.value?.image?.src}`,
      },
      { property: 'og:description', content: seo.value?.metadescription },
      {
        hid: 'description',
        name: 'description',
        content: `${
          seo.value?.metadescription || seo.value?.metatitle || title.value
        }. Смотреть ${
          route.path.split('/').includes('films') ? 'фильмы' : 'видеолекции'
        } по истории РФ для школьников и студентов на сайте Федерального портала истории России.`,
      },
      {
        'http-equiv': 'Last-Modified',
        content: new Date(seo.value?.last_modified).toUTCString(),
      },
      {
        hid: 'robots',
        name: 'robots',
        content: robots.value,
      },
    ],
    link: [
      {
        rel: 'canonical',
        href: `${useHostApp()}${route.path}`,
      },
    ],
  })),
);

useSchemaOrg(() =>
  defineWebPage({
    '@type': 'VideoObject',
    uploadDate: new Date(film.value?.published_at),
    duration: secondsToISO(film.value?.duration),
    contentUrl: film.value?.video_code,
    thumbnailUrl: `${useHostApp()}${film.value?.image?.src}`,
    publisher: {
      '@type': 'Organization',
      '@id': film.value?.entity_source?.url,
      name: film.value?.entity_source?.name,
      url: film.value?.entity_source?.url,
    },
  }),
);
</script>
